import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Page,
  Layout,
  Card,
  Button,
  Badge,
  EmptyState,
  ResourceList,
  ResourceItem,
  Text,
  BlockStack,
  InlineStack,
  Modal,
  Popover,
  Box,
} from '@shopify/polaris';
import { PlusIcon, DeleteIcon, QuestionCircleIcon } from '@shopify/polaris-icons';

import { useStore } from '../providers/StoreProvider';
import { experimentApi } from '../utils/api';
import AppLayout from '../components/AppLayout';
import PremiumFeatureLock from '../components/PremiumFeatureLock';

const STATUS_TONE = {
  draft: undefined,
  running: 'success',
  stopped: 'attention',
};

function Experiments() {
  const navigate = useNavigate();
  const { experiments, isPremium, fetchExperiments } = useStore();

  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [selectedExperiment, setSelectedExperiment] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [helpPopoverActive, setHelpPopoverActive] = useState(false);

  const toggleHelpPopover = useCallback(() => setHelpPopoverActive((active) => !active), []);

  const helpActivator = (
    <Button
      icon={QuestionCircleIcon}
      variant="plain"
      onClick={toggleHelpPopover}
      accessibilityLabel="Learn about A/B testing"
    />
  );

  const helpContent = (
    <Box padding="400" maxWidth="320px">
      <BlockStack gap="300">
        <Text variant="headingMd" as="h3">What is A/B Testing?</Text>
        <Text>
          A/B testing lets you compare two versions of your shipping bar to see which one performs better.
        </Text>
        <Text variant="headingSm" as="h4">How to boost AOV:</Text>
        <BlockStack gap="200">
          <Text>• <strong>Test different thresholds</strong> - Try $50 vs $75 to find the sweet spot</Text>
          <Text>• <strong>Test messaging</strong> - Compare "Free shipping at $50" vs "You're $X away from free shipping!"</Text>
          <Text>• <strong>Test designs</strong> - Bold colors vs subtle, top bar vs bottom bar</Text>
          <Text>• <strong>Test urgency</strong> - Add countdown timers or limited-time offers</Text>
        </BlockStack>
        <Text tone="subdued">
          Run experiments for at least 7 days with 1,000+ visitors for reliable results.
        </Text>
      </BlockStack>
    </Box>
  );

  const handleStart = useCallback(async (id) => {
    setIsProcessing(true);
    try {
      await experimentApi.start(id);
      await fetchExperiments();
    } catch (err) {
      console.error('Failed to start experiment:', err);
    }
    setIsProcessing(false);
  }, [fetchExperiments]);

  const handleStop = useCallback(async (id) => {
    setIsProcessing(true);
    try {
      await experimentApi.stop(id);
      await fetchExperiments();
    } catch (err) {
      console.error('Failed to stop experiment:', err);
    }
    setIsProcessing(false);
  }, [fetchExperiments]);

  const handleDelete = useCallback(async () => {
    if (!selectedExperiment) return;
    setIsProcessing(true);
    try {
      await experimentApi.delete(selectedExperiment.id);
      await fetchExperiments();
    } catch (err) {
      console.error('Failed to delete experiment:', err);
    }
    setIsProcessing(false);
    setDeleteModalOpen(false);
    setSelectedExperiment(null);
  }, [selectedExperiment, fetchExperiments]);

  if (!isPremium) {
    return (
      <AppLayout>
        <Page
          title="A/B Tests"
          titleMetadata={
            <Popover
              active={helpPopoverActive}
              activator={helpActivator}
              onClose={toggleHelpPopover}
              preferredAlignment="left"
            >
              {helpContent}
            </Popover>
          }
        >
          <PremiumFeatureLock isPremium={false} feature="A/B Testing" fullPage>
            <EmptyState
              heading="Unlock A/B Testing"
              action={{
                content: 'Upgrade to Premium',
                onAction: () => navigate('/app/billing'),
              }}
              image="https://cdn.shopify.com/s/files/1/0262/4071/2726/files/emptystate-files.png"
            >
              <p>
                Compare different shipping bars to find which performs best.
                Upgrade to Premium to start running experiments.
              </p>
            </EmptyState>
          </PremiumFeatureLock>
        </Page>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <Page
        title="A/B Tests"
        titleMetadata={
          <Popover
            active={helpPopoverActive}
            activator={helpActivator}
            onClose={toggleHelpPopover}
            preferredAlignment="left"
          >
            {helpContent}
          </Popover>
        }
        primaryAction={{
          content: 'Create experiment',
          icon: PlusIcon,
          onAction: () => navigate('/app/experiments/new'),
        }}
      >
        <Layout>
          <Layout.Section>
            {experiments.length === 0 ? (
              <Card>
                <EmptyState
                  heading="No experiments yet"
                  action={{
                    content: 'Create experiment',
                    onAction: () => navigate('/app/experiments/new'),
                  }}
                  image="https://cdn.shopify.com/s/files/1/0262/4071/2726/files/emptystate-files.png"
                >
                  <p>
                    Create an A/B test to compare different shipping bars
                    and find which converts best.
                  </p>
                </EmptyState>
              </Card>
            ) : (
              <Card padding="0">
                <ResourceList
                  items={experiments}
                  renderItem={(experiment) => {
                    const barNames = (experiment.bars || []).map(b => b.name).join(', ');
                    const isRunning = experiment.status === 'running';
                    const isDraft = experiment.status === 'draft';

                    return (
                      <ResourceItem
                        id={String(experiment.id)}
                        onClick={() => navigate(`/app/experiments/${experiment.id}`)}
                      >
                        <InlineStack align="space-between" blockAlign="center">
                          <BlockStack gap="100">
                            <InlineStack gap="200" blockAlign="center">
                              <Text variant="bodyMd" fontWeight="bold">
                                {experiment.name}
                              </Text>
                              <Badge tone={STATUS_TONE[experiment.status]}>
                                {experiment.status.charAt(0).toUpperCase() + experiment.status.slice(1)}
                              </Badge>
                              {experiment.target_country && (
                                <Badge>{experiment.target_country}</Badge>
                              )}
                            </InlineStack>
                            <Text tone="subdued" variant="bodySm">
                              Bars: {barNames || 'None'}
                            </Text>
                          </BlockStack>
                          <InlineStack gap="200">
                            {isDraft && (
                              <Button
                                size="slim"
                                onClick={(e) => { e.stopPropagation(); handleStart(experiment.id); }}
                                loading={isProcessing}
                              >
                                Start
                              </Button>
                            )}
                            {isRunning && (
                              <Button
                                size="slim"
                                tone="critical"
                                onClick={(e) => { e.stopPropagation(); handleStop(experiment.id); }}
                                loading={isProcessing}
                              >
                                Stop
                              </Button>
                            )}
                            {experiment.status === 'stopped' && (
                              <Button
                                size="slim"
                                onClick={(e) => { e.stopPropagation(); handleStart(experiment.id); }}
                                loading={isProcessing}
                              >
                                Restart
                              </Button>
                            )}
                            {!isRunning && (
                              <Button
                                size="slim"
                                icon={DeleteIcon}
                                tone="critical"
                                variant="plain"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setSelectedExperiment(experiment);
                                  setDeleteModalOpen(true);
                                }}
                              />
                            )}
                          </InlineStack>
                        </InlineStack>
                      </ResourceItem>
                    );
                  }}
                />
              </Card>
            )}
          </Layout.Section>
        </Layout>

        <Modal
          open={deleteModalOpen}
          onClose={() => setDeleteModalOpen(false)}
          title="Delete experiment?"
          primaryAction={{
            content: 'Delete',
            destructive: true,
            onAction: handleDelete,
            loading: isProcessing,
          }}
          secondaryActions={[
            { content: 'Cancel', onAction: () => setDeleteModalOpen(false) },
          ]}
        >
          <Modal.Section>
            <Text>
              Are you sure you want to delete "{selectedExperiment?.name}"?
              This cannot be undone. Analytics data for the individual bars will be preserved.
            </Text>
          </Modal.Section>
        </Modal>
      </Page>
    </AppLayout>
  );
}

export default Experiments;
